import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TextField from '@mui/material/TextField';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 120 },
    { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 180 },
    { field: 'organization', headerName: 'Organization', flex: 1, minWidth: 120 },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 120 },
    { field: 'status', headerName: 'Status', flex: 0.8, minWidth: 120 },
    { field: 'createdAt', headerName: 'Created At', flex: 1, minWidth: 160 },
    {
        field: 'action',
        headerName: 'Actions',
        flex: 1,
        minWidth: 260,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', whiteSpace: 'nowrap' }}>
                <Tooltip title="Edit">
                    <span>
                        <IconButton size="small" color="primary" onClick={() => params.row.onEdit(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Delete">
                    <span>
                        <IconButton size="small" color="error" onClick={() => params.row.onDelete(params.row)} disabled={params.row.role === 'SUPER ADMIN'}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Resend Invite">
                    <span>
                        <IconButton size="small" onClick={() => params.row.onResendInvite(params.row)} disabled={params.row.role === 'SUPER ADMIN' || params.row.status !== 'INVITED'}>
                            <MailOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        ),
    },
];

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '', organization: '', role: 'ADMIN' });
    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', organization: '', role: 'ADMIN' });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/api/admin-users');
            if (res.status === 403) {
                console.error('403 Forbidden: Check backend role, token, and user permissions.');
            }
            const data = res.data;
            setUsers((Array.isArray(data) ? data : []).map(u => ({
                ...u,
                onEdit: handleEditClick,
                onDelete: handleDeleteClick,
                onResendInvite: handleResendInvite
            })));
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load admin users.', severity: 'error' });
            console.error('Error loading users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleResendInvite = async (user) => {
        try {
            setLoading(true);
            const res = await api.post(`/admin/api/admin-users/${user.id}/resend`);
            const payload = res.data;
            setSnackbar({ open: true, message: 'Invite resent successfully.', severity: 'success' });
            if (payload && payload.inviteLink) console.info('Invite link:', payload.inviteLink);
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : 'Failed to resend invite.') || 'Failed to resend invite.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInviteClick = () => {
        setInviteForm({ firstName: '', lastName: '', email: '', organization: '', role: 'ADMIN' });
        setInviteDialogOpen(true);
    };

    const handleInviteConfirm = async () => {
        try {
            setLoading(true);
            await api.post('/admin/api/admin-users/invite', inviteForm);
            setSnackbar({ open: true, message: 'Invite sent successfully.', severity: 'success' });
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : 'Failed to send invite.') || 'Failed to send invite.', severity: 'error' });
        } finally {
            setLoading(false);
            setInviteDialogOpen(false);
        }
    };

    const handleEditClick = (user) => {
        setSelectedUser(user);
        setEditForm({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            organization: user.organization,
            role: user.role
        });
        setEditDialogOpen(true);
    };

    const handleEditConfirm = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            await api.put(`/admin/api/admin-users/${selectedUser.id}`, editForm);
            setSnackbar({ open: true, message: 'User updated successfully.', severity: 'success' });
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : 'Failed to update user.') || 'Failed to update user.', severity: 'error' });
        } finally {
            setLoading(false);
            setEditDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            await api.delete(`/admin/api/admin-users/${selectedUser.id}`);
            setSnackbar({ open: true, message: 'User deleted successfully.', severity: 'success' });
            loadUsers();
        } catch (err) {
            const errMsg = err.response?.data ?? err.message;
            setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : 'Failed to delete user.') || 'Failed to delete user.', severity: 'error' });
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleInviteDialogClose = () => setInviteDialogOpen(false);
    const handleEditDialogClose = () => setEditDialogOpen(false);
    const handleDeleteDialogClose = () => setDeleteDialogOpen(false);
    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h4" component="h2">
                    Admin User Management
                </Typography>
                <Button variant="contained" size="small" onClick={handleInviteClick}>Invite Admin</Button>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadUsers} disabled={loading}>Refresh</Button>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': { fontWeight: 400, textDecoration: 'none' },
                    }}
                />
            </Box>
            {/* Invite Dialog */}
            <Dialog open={inviteDialogOpen} onClose={handleInviteDialogClose} fullWidth maxWidth="md" PaperProps={{ sx: { width: '600px', maxWidth: '90%' } }}>
                <DialogTitle>Invite Admin User</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="First Name" value={inviteForm.firstName} onChange={e => setInviteForm(f => ({ ...f, firstName: e.target.value }))} fullWidth />
                        <TextField label="Last Name" value={inviteForm.lastName} onChange={e => setInviteForm(f => ({ ...f, lastName: e.target.value }))} fullWidth />
                        <TextField label="Email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} fullWidth />
                        <TextField label="Organization" value={inviteForm.organization} onChange={e => setInviteForm(f => ({ ...f, organization: e.target.value }))} fullWidth />
                        <TextField label="Role" value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))} fullWidth select SelectProps={{ native: true }}>
                            <option value="ADMIN">ADMIN</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleInviteDialogClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleInviteConfirm}>Send Invite</Button>
                </DialogActions>
            </Dialog>
            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditDialogClose}>
                <DialogTitle>Edit Admin User</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="First Name" value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} fullWidth />
                        <TextField label="Last Name" value={editForm.lastName} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} fullWidth />
                        <TextField label="Email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} fullWidth />
                        <TextField label="Organization" value={editForm.organization} onChange={e => setEditForm(f => ({ ...f, organization: e.target.value }))} fullWidth />
                        <TextField label="Role" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} fullWidth select SelectProps={{ native: true }}>
                            <option value="ADMIN">ADMIN</option>
                            <option value="SUPER ADMIN">SUPER ADMIN</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditDialogClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditConfirm}>Save Changes</Button>
                </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete Admin User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete admin user "{selectedUser?.firstName} {selectedUser?.lastName}" (ID: {selectedUser?.id})? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminUserManagement;
